import { useEffect, useMemo, useState } from 'react';

import { StarIcon } from '@hugeicons-pro/core-solid-rounded';
import {
  Copy01Icon,
  Delete02Icon,
  GridViewIcon,
  LeftToRightListBulletIcon,
  Mail01Icon,
  MailBlock01Icon,
  Message01Icon,
  MessageBlockedIcon,
  Notification01Icon,
  NotificationBlock01Icon,
  PaintBoardIcon,
  PencilEdit01Icon,
  TimeScheduleIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { useToast } from 'hooks/toast';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Alert from 'components/Alert/Alert';
import { Button, SearchInput } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';
import { IconCountChip } from 'components/IconCountChip/IconCountChip';
import { ItemOverviewCard } from 'components/ItemOverviewCard/ItemOverviewCard';
import StatusPill from 'components/Pills/StatusPills';
import { Table } from 'components/Table/Table';

import {
  createBrand,
  deleteBrand,
  duplicateBrand,
  getBrands,
  selectBrands,
} from 'api/brands-slice';

import { CreateBrandModal } from './CreateBrandModal/CreateBrandModal';
import { DuplicateBrandModal } from './DuplicateBrandModal/DuplicateBrandModal';

export const Branding = ({ setLoading = () => {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showToast = useToast();
  const brands = useSelector(selectBrands);

  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('card');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [brandToDuplicate, setBrandToDuplicate] = useState(null);

  useEffect(() => {
    setLoading(true);
    dispatch(getBrands()).then(() => setLoading(false));
  }, []);

  const filteredBrands = useMemo(() => {
    if (!search) return brands;
    const q = search.toLowerCase();
    return brands.filter(
      b => b.name?.toLowerCase().includes(q) || b.brand_email?.toLowerCase().includes(q),
    );
  }, [brands, search]);

  const handleCreate = async data => {
    setCreating(true);
    try {
      const { payload: newBrand } = await dispatch(createBrand(data));
      if (newBrand?.id) {
        setShowCreateModal(false);
        navigate(`/branding_details/${newBrand.id}`);
      }
    } catch (e) {
      showToast(
        'Error',
        typeof e?.message === 'string' ? e.message : t('branding.saveError'),
        'error',
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async data => {
    setCreating(true);
    try {
      const { payload: newBrand } = await dispatch(
        duplicateBrand({ id: brandToDuplicate.id, data }),
      );
      if (newBrand?.id) {
        setBrandToDuplicate(null);
        navigate(`/branding_details/${newBrand.id}`);
      }
    } catch (e) {
      showToast(
        'Error',
        typeof e?.message === 'string' ? e.message : t('branding.saveError'),
        'error',
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    await dispatch(deleteBrand(brandToDelete));
    setBrandToDelete(null);
    setLoading(false);
  };

  const renderCardView = () => {
    if (filteredBrands.length === 0) {
      return (
        <p className="text-xl text-neutral-5 py-12 text-center">{t('branding.noBrandsFound')}</p>
      );
    }

    return (
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filteredBrands.map(brand => {
          const updatedAt = brand.updated_at || brand.created_at;

          return (
            <ItemOverviewCard
              key={brand.id}
              image={brand?.brand_logo}
              displayNoImage
              title={brand.name}
              description={brand?.brand_email || 'No brand email set'}
              tags={[
                ...(brand.default
                  ? [
                      <StatusPill
                        status="warning"
                        text="Default"
                        className="px-4"
                        icon={StarIcon}
                        iconColor="text-orange-3"
                        noBorder
                      />,
                    ]
                  : []),
              ]}
              subtext={
                updatedAt ? `Last updated: ${moment(updatedAt).format('DD/MM/YYYY')}` : undefined
              }
              onClick={() => navigate(`/branding_details/${brand.id}?tab=general`)}
              actions={[
                {
                  label: 'Edit',
                  icon: PencilEdit01Icon,
                  onClick: () => navigate(`/branding_details/${brand.id}?tab=general`),
                },
                {
                  label: 'Duplicate',
                  icon: Copy01Icon,
                  onClick: () => setBrandToDuplicate(brand),
                },
                {
                  label: 'Delete',
                  icon: Delete02Icon,
                  type: 'error',
                  onClick: () => setBrandToDelete(brand.id),
                },
              ]}
            />
          );
        })}
      </div>
    );
  };

  const renderTableView = () => {
    if (filteredBrands.length === 0) {
      return (
        <p className="text-xl text-neutral-5 py-12 text-center">{t('branding.noBrandsFound')}</p>
      );
    }

    return (
      <Table.Context>
        <Table.TableContent className="overflow-x-auto">
          <Table>
            <Table.Head>
              <tr>
                <Table.Header noBorder>{t('branding.table.name')}</Table.Header>
                <Table.Header noBorder>{t('branding.table.displayName')}</Table.Header>
                <Table.Header noBorder>{t('branding.table.email')}</Table.Header>
                <Table.Header noBorder>{t('branding.table.status')}</Table.Header>
                <Table.Header noBorder>{t('branding.table.updated')}</Table.Header>
                <Table.Header noBorder align="right">
                  {t('branding.table.actions')}
                </Table.Header>
              </tr>
            </Table.Head>
            <Table.Body>
              {filteredBrands.map(brand => (
                <Table.Row key={brand.id}>
                  <Table.Col>
                    <div
                      className="cursor-pointer hover:text-primary-6 flex gap-4 items-center"
                      onClick={() => navigate(`/branding_details/${brand.id}?tab=general`)}>
                      <span className="font-medium text-primary-4 flex items-center gap-2">
                        {brand?.brand_logo && (
                          <img
                            src={brand.brand_logo}
                            alt=""
                            className="h-6 w-6 object-contain rounded"
                          />
                        )}
                        <span className="font-medium text-primary-4">{brand.name}</span>
                      </span>
                      {brand.default && (
                        <StatusPill
                          status="warning"
                          text="Default"
                          className="w-fit"
                          icon={StarIcon}
                          iconColor="text-orange-3"
                          noBorder
                        />
                      )}
                    </div>
                  </Table.Col>
                  <Table.Col>
                    <span className="text-neutral-6 text-xl">{brand?.brand_name || '—'}</span>
                  </Table.Col>
                  <Table.Col>
                    <span className="text-neutral-6 text-xl">{brand?.brand_email || '—'}</span>
                  </Table.Col>
                  <Table.Col>
                    <div className="flex items-center gap-2">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {brand?.use_email_communication
                              ? 'Email communication turned on'
                              : 'Email communication turned off'}
                          </Tooltip>
                        }>
                        <span className="inline-flex">
                          <IconCountChip
                            icon={brand?.use_email_communication ? Mail01Icon : MailBlock01Icon}
                            backgroundColor="bg-primary-4"
                            iconColor={brand?.use_email_communication ? undefined : 'text-red-2'}
                          />
                        </span>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {brand?.use_sms_communication
                              ? 'SMS communication turned on'
                              : 'SMS communication turned off'}
                          </Tooltip>
                        }>
                        <span className="inline-flex">
                          <IconCountChip
                            icon={brand?.use_sms_communication ? Message01Icon : MessageBlockedIcon}
                            backgroundColor="bg-primary-4"
                            iconColor={brand?.use_sms_communication ? undefined : 'text-red-2'}
                          />
                        </span>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {brand?.reminders_active
                              ? 'Reminders turned on'
                              : 'Reminders turned off'}
                          </Tooltip>
                        }>
                        <span className="inline-flex">
                          <IconCountChip
                            icon={
                              brand?.reminders_active ? Notification01Icon : NotificationBlock01Icon
                            }
                            backgroundColor="bg-primary-4"
                            iconColor={brand?.reminders_active ? undefined : 'text-red-2'}
                          />
                        </span>
                      </OverlayTrigger>
                    </div>
                  </Table.Col>
                  <Table.Col>
                    {brand.updated_at || brand.created_at
                      ? moment(brand.updated_at || brand.created_at).format('DD/MM/YYYY')
                      : '—'}
                  </Table.Col>
                  <Table.Col align="right">
                    <div className="flex items-center justify-end gap-2">
                      <IconButton
                        icon={PencilEdit01Icon}
                        prefix="hugeicons"
                        tip="Edit brand"
                        onClick={() => navigate(`/branding_details/${brand.id}?tab=general`)}
                        placement="top"
                      />
                      <IconButton
                        icon={Copy01Icon}
                        prefix="hugeicons"
                        tip="Duplicate brand"
                        onClick={() => setBrandToDuplicate(brand)}
                        placement="top"
                      />
                      <IconButton
                        icon={Delete02Icon}
                        prefix="hugeicons"
                        tip="Delete brand"
                        onClick={() => setBrandToDelete(brand.id)}
                        placement="top"
                        type="delete"
                      />
                    </div>
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Table.TableContent>
      </Table.Context>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Filter bar */}
      <div className="bg-neutral-1 rounded-3xl p-8 border border-neutral-4 shadow-sm flex flex-col gap-8">
        <div className="flex gap-4 items-center">
          <IconComponent icon={PaintBoardIcon} />
          <div className="flex flex-col gap-1">
            <span className="text-3xl font-medium text-neutral-7">Brands</span>
            <p className="text-xl text-text-tertiary">
              Create and manage your companies branding, which is used across candidate and referee
              experiences.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SearchInput
            placeholder={t('branding.searchPlaceholder')}
            value={search}
            onChange={setSearch}
            className="w-full md:w-1/2 lg:w-1/3 xl:w-1/4"
          />

          <div className="flex-1" />

          {/* View mode toggle */}
          <div className="flex items-center gap-1 border border-neutral-4 rounded-full p-1">
            <button
              className={cn(
                'p-2 rounded-full transition-colors',
                viewMode === 'card'
                  ? 'bg-primary-1 text-primary-6'
                  : 'text-neutral-5 hover:text-neutral-7',
              )}
              onClick={() => setViewMode('card')}>
              <HugeiconsIcon icon={GridViewIcon} size={20} strokeWidth={2} />
            </button>
            <button
              className={cn(
                'p-2 rounded-full transition-colors',
                viewMode === 'table'
                  ? 'bg-primary-1 text-primary-6'
                  : 'text-neutral-5 hover:text-neutral-7',
              )}
              onClick={() => setViewMode('table')}>
              <HugeiconsIcon icon={LeftToRightListBulletIcon} size={20} strokeWidth={2} />
            </button>
          </div>

          <Button icon="plus" small onClick={() => setShowCreateModal(true)}>
            {t('branding.createBrand')}
          </Button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'card' ? renderCardView() : renderTableView()}

      {/* Delete confirmation */}
      {brandToDelete && (
        <Alert
          show={!!brandToDelete}
          title={t('branding.deleteConfirm.title')}
          message={t('branding.deleteConfirm.message')}
          ok={t('common.delete')}
          cancel={t('common.cancel')}
          icon={Delete02Icon}
          showConfirmationInput
          type="error"
          onOk={handleDelete}
          onCancel={() => setBrandToDelete(null)}
        />
      )}

      <CreateBrandModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreate}
        isSubmitting={creating}
      />

      <DuplicateBrandModal
        brand={brandToDuplicate}
        visible={!!brandToDuplicate}
        onClose={() => setBrandToDuplicate(null)}
        onSubmit={handleDuplicate}
        isSubmitting={creating}
      />
    </div>
  );
};
